import { DialogController } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { AsyncErrorHandler, toFixed } from 'lib/ui';
import { MooringOfferDto } from 'models/mooring';
import { FloaterTypeService } from 'services/floater-type-service';
import { MooringService } from 'services/mooring';

export type MooringBridleCalculatorDialogResult = {
  bridles: { id: number; length: number }[];
};

export type MooringBridleCalculatorDialogArgs = {
  mooringId: number;
};

type BuoyBridle = {
  bridle: string;
  bridleId: number;
  buoyNo: string;
  length: number;
};

export type CageMapping = {
  cage: string;
  cageId: number;
  penCircumference: number;
  penCircumferenceOuterRing: number;
  distanceBetweenLines: number;
  calculate: boolean;
  buoys: Record<string, BuoyBridle[]>;
};

@autoinject()
export class MooringBridleCalculatorDialog {
  protected offer: MooringOfferDto;

  data = {
    noBridlesLabel: `3) ${this.t.tr('mooring.noBridles')}`,
    noBridles: 0,

    distanceBridleLinesLabel: `4) ${this.t.tr('mooring.distanceBridleLines')}`,
    // distanceBridleLines: 0,

    gridLineLengthLabel: `5) ${this.t.tr('mooring.gridLineLength')}`,
    gridLineLength: 0,

    mooringDepthLabel: `7) ${this.t.tr('mooring.mooringDepth')}`,
    mooringDepth: 0,
  };

  cages: CageMapping[] = [];

  constructor(
    private t: I18N,
    private mooringService: MooringService,
    private dialogSerivce: DialogController,
    private floaterService: FloaterTypeService
  ) {}

  protected activate({ mooringId }: MooringBridleCalculatorDialogArgs) {
    void this.init(mooringId);
  }

  async init(mooringId: number) {
    await this.getMooring(mooringId);
  }

  @AsyncErrorHandler
  protected async getMooring(mooringId: number) {
    const { data } = await this.mooringService.getMooring(mooringId);

    const [{ data: cages }, { data: bridles }, floaters] = await Promise.all([
      this.mooringService.getCages(data.Id),
      this.mooringService.getBridles(data.Id),
      this.floaterService.getAll(),
    ]);

    // this.offer = data;
    this.data.noBridles = data.SiteSetup.NoBridles;
    // this.data.distanceBridleLines = 8;
    this.data.gridLineLength = data.SiteSetup.GridLineLength;
    this.data.mooringDepth = data.SiteSetup.MooringDepth;

    for (const cage of cages) {
      const cageBridles = bridles?.filter((b) => b.CageId === cage.Id);
      const cageFloater = floaters.find((f) => f.Id === cage.FloaterId);
      const bridlesByBuoyNo = cageBridles.reduce(
        (acc, b) => {
          const buoyNo = b.BuoyNo || '-';
          if (!acc[buoyNo]) {
            acc[buoyNo] = [];
          }

          const br = {
            bridleId: b.Id,
            bridle: b.MoorlogNo,
            buoyNo: b.BuoyNo || '-',
            length: b.CalculatedLength || 0,
          } satisfies BuoyBridle;

          acc[buoyNo].push(br);
          acc[buoyNo].sort((a, b) => a.bridle.localeCompare(b.bridle));

          return acc;
        },
        {} as Record<string, BuoyBridle[]>
      );

      this.cages.push({
        cageId: cage.Id,
        calculate: true,
        cage: cage.MoorlogNo,
        buoys: bridlesByBuoyNo,
        penCircumference: cage.PenCircumference || 0,
        penCircumferenceOuterRing: cageFloater?.CircumferenceOuterRing || 0,
        distanceBetweenLines: cageFloater?.DistanceBetweenLines || 0,
      }); 
    }
  }

  protected enableAllCages() {
    this.cages.forEach((cage) => {
      cage.calculate = true;
    });
  }

  protected disableAllCages() {
    this.cages.forEach((cage) => {
      cage.calculate = false;
    });
  }

  protected onCalculate() {
    for (const cage of this.cages) {
      if (!cage.calculate) {
        continue;
      }

      const cirmuferenceOuterRing = cage.penCircumferenceOuterRing;
      const ditanceBetweenLines = cage.distanceBetweenLines;

      const result = this.calculate(cirmuferenceOuterRing, ditanceBetweenLines);

      Object.values(cage.buoys).forEach((bridles) => {
        const noBridles = bridles.length;

        bridles.forEach((bridle, i) => {
          if (i === 0 || i === noBridles - 1) {
            bridle.length = result.lengthOuterMostBridleToConnection;
          } else {
            bridle.length = result.lengthCenterBridleToConnection;
          }
        });
      });
    }
  }

  protected calculate(cirmuferenceOuterRing: number, ditanceBetweenLines: number) {
    const radius = cirmuferenceOuterRing / (2 * Math.PI);

    const middle = Math.sqrt(((Math.pow(this.data.mooringDepth, 2)) + Math.pow(radius - (this.data.gridLineLength / Math.sqrt(2)), 2)))

    const outer = Math.sqrt((Math.pow(this.data.mooringDepth, 2)) + (Math.pow(radius, 2)) + ((Math.pow(this.data.gridLineLength, 2)) / 2) - (Math.sqrt(2) * this.data.gridLineLength * radius) + (((Math.sqrt(2) * this.data.gridLineLength)/(2 * radius)) * Math.pow(ditanceBetweenLines, 2)));

    const result = {
      lengthCenterBridleToConnection: 0,
      lengthOuterMostBridleToConnection: 0,
    };

    result.lengthCenterBridleToConnection = toFixed(middle);
    result.lengthOuterMostBridleToConnection = toFixed(outer);
    return result;
  }

  protected onAddLengths() {
    const result = this.cages.reduce(
      (acc, cage) => {
        if (!cage.calculate) {
          return acc;
        }
        Object.values(cage.buoys).forEach((bridles) => {
          bridles.forEach((bridle) => {
            acc.push({
              id: bridle.bridleId,
              length: bridle.length,
            });
          });
        });
        return acc;
      },
      [] as MooringBridleCalculatorDialogResult['bridles']
    );
    this.dialogSerivce.close(true, {
      bridles: result,
    } satisfies MooringBridleCalculatorDialogResult);
  }

  protected onCancel() {
    this.dialogSerivce.close(false);
  }

  protected async attached() {}
}
